/**
 * Browser telemetry for every applicant act — what the browser DECLARES about
 * itself and how the applicant moved through the step, attached to each
 * `/collection-flow/events` request as `telemetry`. The server stores it in
 * `kyc_capture_device.client_attributes`, beside what it observed of the
 * request itself (`server_observed`: headers, address → place, User-Agent →
 * words). Ported from seocrown formleads `readClientContext` (`em.js`).
 *
 * Three rules hold everywhere in this file:
 *
 * 1. **Counts and milliseconds, never characters.** The pace listeners count
 *    keystrokes, pastes and corrections; they never read a value, a key or the
 *    clipboard. What the applicant typed is the sealed `DETAILS_SUBMITTED`
 *    payload's business, not this file's.
 * 2. **The applicant token never leaves in telemetry.** It is in the URL
 *    (`?token=`, `/resume/:token`, `/u/:code`), so the path is reduced to a
 *    template and only allowlisted query parameters are kept. A referrer keeps
 *    its host and path, never its query.
 * 3. **Declared, not derived.** Everything here is a property the browser
 *    exposes (`navigator.deviceMemory`, `hardwareConcurrency`, the WebGL
 *    renderer string). Nothing draws to a canvas or plays audio to hash the
 *    result — the server stores no fingerprint, and the device report's
 *    same-device signal is not fed from here.
 *
 * RUM (real-user monitoring) rides the same object: main-thread blocking (long
 * tasks / long animation frames), JS and resource errors, latency per `/api`
 * endpoint and a resource summary, all cumulative since the page loaded — and
 * {@link armExitBeacon} sends a snapshot when the applicant LEAVES a step
 * without submitting it, the case that otherwise leaves no trace at all. Error
 * text and every URL pass through {@link scrub} first: an error message can
 * quote a URL, and a URL here can hold the token.
 *
 * Every reading is best-effort: an API the browser lacks (Safari has no
 * `deviceMemory`, Firefox no `userAgentData`) yields an absent key, never an
 * error, and nothing here can fail the request it rides on.
 */

import { uuidv7 } from './uuidv7';

/** Bumped whenever the shape below changes; the server stores it verbatim. */
export const TELEMETRY_SCHEMA = '1';

type Scalar = string | number | boolean;
type Section = Record<string, Scalar | string[] | Record<string, number>>;
export type Telemetry = Record<string, Section | string>;

/** Query parameters worth keeping: campaign tags and ad click ids. Nothing else
 *  in the URL is recorded — `token` above all. */
const QUERY_ALLOWLIST = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_term',
  'utm_content',
  'utm_source_platform',
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'fbclid',
  'ttclid',
  'li_fat_id',
  'ref',
  'partner_id',
] as const;

const MAX_TEXT = 256;
const MAX_LIST = 20;
/** No pointer/key activity for this long counts as idle time. */
const IDLE_AFTER_MS = 15_000;
const DEVICE_KEY = 'trustsy.device';

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value.slice(0, MAX_TEXT) : undefined;
const list = (values: unknown): string[] | undefined =>
  Array.isArray(values)
    ? values.filter((v): v is string => typeof v === 'string' && v !== '').slice(0, MAX_LIST)
    : undefined;
const iso = (ms: number | undefined): string | undefined =>
  typeof ms === 'number' && ms > 0 ? new Date(ms).toISOString() : undefined;

/**
 * Drop the owner's name a camera label can carry — macOS Continuity Camera
 * names the phone ("Jane’s iPhone Camera") — keeping the device part. The
 * server repeats this (`device_risk::scrub_camera_label`): the browser is not
 * trusted to have done it.
 */
export function scrubCameraLabel(label: string): string {
  const trimmed = label.trim();
  const ends = ['’s ', "'s "]
    .map((marker) => {
      const at = trimmed.indexOf(marker);
      return at < 0 ? -1 : at + marker.length;
    })
    .filter((end) => end >= 0);
  const rest = ends.length > 0 ? trimmed.slice(Math.min(...ends)) : trimmed;
  return [...rest].slice(0, 80).join('');
}

/**
 * Remember which camera the liveness step opened. The LABEL is the point: a
 * virtual camera names itself ("OBS Virtual Camera", "ManyCam Virtual Webcam"),
 * which is the most direct injection signal a browser exposes. Never the
 * `deviceId` / `groupId` — those are per-origin identifiers, not properties.
 */
export function noteCamera(track: MediaStreamTrack | undefined): void {
  if (!track) return;
  let settings: MediaTrackSettings = {};
  try {
    settings = track.getSettings();
  } catch {
    /* a browser without getSettings still has a label */
  }
  state.camera = {
    label: typeof track.label === 'string' ? text(scrubCameraLabel(track.label)) : undefined,
    width: settings.width,
    height: settings.height,
    frame_rate: typeof settings.frameRate === 'number' ? Math.round(settings.frameRate) : undefined,
  };
}

/** Copy only defined, finite, non-empty values — an absent key means "not observed". */
function section(build: () => Record<string, unknown>): Section {
  const out: Section = {};
  try {
    for (const [key, value] of Object.entries(build())) {
      if (value === undefined || value === null || value === '') continue;
      if (typeof value === 'number' && !Number.isFinite(value)) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      out[key] = value as Section[string];
    }
  } catch {
    /* an API this browser lacks = no keys for this section */
  }
  return out;
}

// ------------------------------------------------------- the network trace

/**
 * Cloudflare's description of the connection that fetched it. Only the browser
 * can ask it: WARP (`warp=on|plus`), a Zero Trust Gateway (`gateway=on`) and a
 * remote isolated browser (`rbi=on`) all exit from Cloudflare's AS13335, which
 * the server cannot list as a VPN without also listing iCloud Private Relay.
 * Served with `access-control-allow-origin: *`.
 */
export const CLOUDFLARE_TRACE_URL = 'https://www.cloudflare.com/cdn-cgi/trace';

/** Kept from the trace; the rest (UA, TLS, timestamps) repeats what the server
 *  records. The server keeps the same list (`cloudflare_trace::FIELDS`). */
const TRACE_FIELDS = ['ip', 'loc', 'colo', 'warp', 'gateway', 'rbi'];

/** `key=value` lines → the kept fields. */
export function parseTrace(body: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of body.split('\n')) {
    const at = line.indexOf('=');
    const key = line.slice(0, at).trim();
    const value = line.slice(at + 1).trim();
    if (at > 0 && TRACE_FIELDS.includes(key) && value) out[key] = value.slice(0, 64);
  }
  return out;
}

/** Ask once per page load (`primeTelemetry`), never retried, no cookies, no
 *  referrer (the page URL holds the token). Never rejects: a refusal or a
 *  timeout leaves the section absent. */
export async function readCloudflareTrace(): Promise<void> {
  try {
    const response = await fetch(CLOUDFLARE_TRACE_URL, {
      mode: 'cors',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      cache: 'no-store',
      signal: AbortSignal.timeout?.(5000),
    });
    const trace = parseTrace(response.ok ? await response.text() : '');
    if (Object.keys(trace).length > 0) state.trace = trace;
  } catch {
    /* blocked, offline or no fetch = no section */
  }
}

// ------------------------------------------------------------- the path

/** `/resume/<token>` → `/resume/:token`, `/u/<code>` → `/u/:code`; any other
 *  path as-is. Both route parameters in `App.tsx` ARE the credential — a flow
 *  code is the token re-encoded, and one with no digit in it would slip past
 *  `templatePath`'s id rule. */
export function pathTemplate(pathname: string): string {
  return pathname
    .replace(/^\/resume\/[^/]+/, '/resume/:token')
    .replace(/^\/u\/[^/]+/, '/u/:code');
}

/** Allowlisted query parameters only. */
export function allowlistedQuery(search: string): Record<string, string> | undefined {
  const params = new URLSearchParams(search);
  const kept: Record<string, string> = {};
  for (const name of QUERY_ALLOWLIST) {
    const value = text(params.get(name) ?? undefined);
    if (value) kept[name] = value;
  }
  return Object.keys(kept).length > 0 ? kept : undefined;
}

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** A path with every id-like segment (a UUID, or 8+ chars containing a digit)
 *  replaced by `:id` — case ids, file ids and tokens alike. Endpoint latency is
 *  then grouped by ROUTE, and no identifier is stored. */
export function templatePath(pathname: string): string {
  return pathTemplate(pathname)
    .split('/')
    .map((segment) =>
      segment.startsWith(':') || !(/\d/.test(segment) && segment.length >= 8) ? segment : ':id',
    )
    .join('/');
}

/** Error text made safe to store: URLs reduced to origin + templated path,
 *  `token=` values, `/resume/<token>`, `/u/<code>`, UUIDs and any long digit-bearing word
 *  redacted, length capped. Layered on purpose — no one rule may be the only
 *  thing between an applicant credential and the database. */
export function scrub(raw: string): string {
  return raw
    .replace(/https?:\/\/[^\s'"()<>]+/g, (found) => {
      try {
        const url = new URL(found);
        return `${url.origin}${templatePath(url.pathname)}`;
      } catch {
        return ':url';
      }
    })
    .replace(/token=[^&\s'"]+/gi, 'token=:redacted')
    // A bare path quoted outside a URL: the routes whose segment IS the token.
    .replace(/\/resume\/[^\s/'"?#]+/g, '/resume/:token')
    .replace(/\/u\/[^\s/'"?#]+/g, '/u/:code')
    .replace(UUID, ':uuid')
    // Whatever shape an id or credential takes: a long word with a digit in it.
    // Line/column numbers and ordinary words are shorter or digit-free.
    .replace(/(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]{16,}/g, ':id')
    .slice(0, MAX_TEXT);
}

// ------------------------------------------------------------- live state

interface Pace {
  keystrokes: number;
  inputs: number;
  pastes: number;
  corrections: number;
  focusChanges: number;
  taps: number;
  pointerMoves: number;
  scrollMaxPercent: number;
  resizes: number;
  hiddenCount: number;
  hiddenMs: number;
  blurredMs: number;
  idleMs: number;
  idleMaxMs: number;
}

const freshPace = (): Pace => ({
  keystrokes: 0,
  inputs: 0,
  pastes: 0,
  corrections: 0,
  focusChanges: 0,
  taps: 0,
  pointerMoves: 0,
  scrollMaxPercent: 0,
  resizes: 0,
  hiddenCount: 0,
  hiddenMs: 0,
  blurredMs: 0,
  idleMs: 0,
  idleMaxMs: 0,
});

interface ApiTiming {
  count: number;
  total_ms: number;
  max_ms: number;
  failures: number;
  last_status: number;
}

interface Rum {
  longTasks: number;
  longTaskMs: number;
  longTaskMaxMs: number;
  /** Sum over long tasks of the part beyond 50 ms — Total Blocking Time. */
  blockingMs: number;
  loafs: number;
  loafBlockingMs: number;
  loafMaxMs: number;
  jsErrors: number;
  rejections: number;
  resourceErrors: number;
  /** The most recent errors, scrubbed — newest last. */
  errors: string[];
  resources: number;
  resourceBytes: number;
  resourceByType: Record<string, number>;
  slowestResource: string;
  slowestResourceMs: number;
  api: Map<string, ApiTiming>;
}

const MAX_ERRORS = 5;
const MAX_API_ROUTES = 20;

const freshRum = (): Rum => ({
  longTasks: 0,
  longTaskMs: 0,
  longTaskMaxMs: 0,
  blockingMs: 0,
  loafs: 0,
  loafBlockingMs: 0,
  loafMaxMs: 0,
  jsErrors: 0,
  rejections: 0,
  resourceErrors: 0,
  errors: [],
  resources: 0,
  resourceBytes: 0,
  resourceByType: {},
  slowestResource: '',
  slowestResourceMs: 0,
  api: new Map(),
});

interface State {
  primed: boolean;
  pageStartedAt: number;
  stepStartedAt: number;
  sequence: number;
  /** Totals since the page loaded. */
  session: Pace;
  /** Totals since the previous act — reset by every `collectTelemetry`. */
  step: Pace;
  hiddenSince: number | null;
  blurredSince: number | null;
  lastActivityAt: number;
  async: Record<string, Scalar>;
  highEntropy: Record<string, unknown>;
  vitals: { lcp?: number; cls: number; inp?: number; fcp?: number };
  device: { id?: string; firstSeenAt?: number; visits?: number };
  rum: Rum;
  /** The camera the liveness step actually opened — see {@link noteCamera}. */
  camera: { label?: string; width?: number; height?: number; frame_rate?: number } | null;
  /** What Cloudflare saw of this browser's connection — see {@link parseTrace}. */
  trace: Record<string, string> | null;
}

const state: State = {
  primed: false,
  pageStartedAt: Date.now(),
  stepStartedAt: Date.now(),
  sequence: 0,
  session: freshPace(),
  step: freshPace(),
  hiddenSince: null,
  blurredSince: null,
  lastActivityAt: Date.now(),
  async: {},
  highEntropy: {},
  vitals: { cls: 0 },
  device: {},
  rum: freshRum(),
  camera: null,
  trace: null,
};

function bump(key: keyof Pace, by = 1): void {
  state.session[key] += by;
  state.step[key] += by;
}

/** A pointer or key event: closes any idle gap that just ended. */
function activity(now: number): void {
  const gap = now - state.lastActivityAt;
  if (gap > IDLE_AFTER_MS) {
    bump('idleMs', gap);
    state.session.idleMaxMs = Math.max(state.session.idleMaxMs, gap);
    state.step.idleMaxMs = Math.max(state.step.idleMaxMs, gap);
  }
  state.lastActivityAt = now;
}


/** A per-browser id kept in localStorage — the same browser across visits and
 *  cases. A random id, not derived from anything the browser is. */
function loadDevice(now: number): void {
  try {
    const raw = window.localStorage.getItem(DEVICE_KEY);
    const known = raw ? (JSON.parse(raw) as { id?: string; first?: number; visits?: number }) : {};
    const record = {
      id: typeof known.id === 'string' ? known.id : uuidv7(),
      first: typeof known.first === 'number' ? known.first : now,
      visits: (typeof known.visits === 'number' ? known.visits : 0) + 1,
    };
    window.localStorage.setItem(DEVICE_KEY, JSON.stringify(record));
    state.device = { id: record.id, firstSeenAt: record.first, visits: record.visits };
  } catch {
    /* storage blocked (private mode, partitioned iframe) = no device id */
  }
}

/**
 * This browser's random id — the one telemetry reports — created on first use.
 * A reusable workflow link sends it so the server can count this browser's
 * failed attempts (it stores a digest). Storage blocked = a fresh id each time.
 */
export function browserId(): string {
  try {
    const raw = window.localStorage.getItem(DEVICE_KEY);
    const known = raw ? (JSON.parse(raw) as { id?: string }) : {};
    if (typeof known.id === 'string' && known.id.length >= 16) return known.id;
  } catch {
    /* fall through: create one */
  }
  loadDevice(Date.now());
  return state.device?.id ?? uuidv7();
}

function stash(read: () => Promise<Record<string, Scalar | undefined>> | undefined): void {
  try {
    void read()
      ?.then((values) => {
        for (const [key, value] of Object.entries(values)) {
          if (value !== undefined) state.async[key] = value;
        }
      })
      .catch(() => {
        /* refused = no key */
      });
  } catch {
    /* no such API = no key */
  }
}

function noteError(kind: string, detail: string): void {
  const errors = state.rum.errors;
  errors.push(scrub(`${kind}: ${detail}`));
  if (errors.length > MAX_ERRORS) errors.shift();
}

/** One finished network request — the resource summary and, for our own
 *  `/api` calls, latency per route. */
export function noteResource(entry: PerformanceResourceTiming): void {
  const rum = state.rum;
  rum.resources += 1;
  rum.resourceBytes += entry.transferSize || 0;
  const type = text(entry.initiatorType) ?? 'other';
  if (type in rum.resourceByType || Object.keys(rum.resourceByType).length < MAX_LIST) {
    rum.resourceByType[type] = (rum.resourceByType[type] ?? 0) + 1;
  }
  let url: URL;
  try {
    url = new URL(entry.name, location.href);
  } catch {
    return;
  }
  const route = templatePath(url.pathname);
  if (entry.duration > rum.slowestResourceMs) {
    rum.slowestResourceMs = Math.round(entry.duration);
    rum.slowestResource = `${url.origin === location.origin ? '' : url.origin}${route}`.slice(0, MAX_TEXT);
  }
  if (!route.startsWith('/api/')) return;
  const known = rum.api.get(route);
  if (!known && rum.api.size >= MAX_API_ROUTES) return;
  const timing = known ?? { count: 0, total_ms: 0, max_ms: 0, failures: 0, last_status: 0 };
  timing.count += 1;
  timing.total_ms += entry.duration;
  timing.max_ms = Math.max(timing.max_ms, entry.duration);
  // `responseStatus` is Chromium-only; elsewhere the status is simply absent.
  const status = (entry as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;
  if (typeof status === 'number' && status > 0) {
    timing.last_status = status;
    if (status >= 400) timing.failures += 1;
  }
  rum.api.set(route, timing);
}

type UaData = {
  mobile?: boolean;
  platform?: string;
  brands?: { brand: string; version: string }[];
  getHighEntropyValues?: (hints: string[]) => Promise<Record<string, unknown>>;
};

/**
 * Start listening. Idempotent; call once before the first step renders, so the
 * first step's pace and the page's web vitals are measured from the start.
 */
export function primeTelemetry(): void {
  if (state.primed || typeof window === 'undefined') return;
  state.primed = true;
  const now = Date.now();
  state.pageStartedAt = now;
  state.stepStartedAt = now;
  state.lastActivityAt = now;
  loadDevice(now);

  const nav = navigator as unknown as Record<string, unknown>;
  const opts = { capture: true, passive: true } as const;
  document.addEventListener(
    'keydown',
    () => {
      bump('keystrokes');
      activity(Date.now());
    },
    opts,
  );
  document.addEventListener(
    'input',
    (event) => {
      bump('inputs');
      const kind = (event as InputEvent).inputType;
      if (typeof kind === 'string' && kind.startsWith('delete')) bump('corrections');
    },
    opts,
  );
  // The event is counted; `clipboardData` is never read.
  document.addEventListener('paste', () => bump('pastes'), opts);
  document.addEventListener('focusin', () => bump('focusChanges'), opts);
  document.addEventListener(
    'pointerdown',
    () => {
      bump('taps');
      activity(Date.now());
    },
    opts,
  );
  let lastMove = 0;
  document.addEventListener(
    'pointermove',
    () => {
      const at = Date.now();
      if (at - lastMove < 100) return; // a count of movements, not of pixels
      lastMove = at;
      bump('pointerMoves');
      activity(at);
    },
    opts,
  );
  window.addEventListener(
    'scroll',
    () => {
      const doc = document.documentElement;
      const depth = Math.min(
        100,
        Math.round(((window.scrollY + window.innerHeight) / Math.max(1, doc.scrollHeight)) * 100),
      );
      state.session.scrollMaxPercent = Math.max(state.session.scrollMaxPercent, depth);
      state.step.scrollMaxPercent = Math.max(state.step.scrollMaxPercent, depth);
    },
    { passive: true },
  );
  window.addEventListener('resize', () => bump('resizes'), { passive: true });
  document.addEventListener('visibilitychange', () => {
    const at = Date.now();
    if (document.visibilityState === 'hidden') {
      bump('hiddenCount');
      state.hiddenSince = at;
    } else if (state.hiddenSince !== null) {
      bump('hiddenMs', at - state.hiddenSince);
      state.hiddenSince = null;
    }
  });
  window.addEventListener('blur', () => {
    state.blurredSince = Date.now();
  });
  window.addEventListener('focus', () => {
    if (state.blurredSince !== null) {
      bump('blurredMs', Date.now() - state.blurredSince);
      state.blurredSince = null;
    }
  });

  void readCloudflareTrace();
  // Values that arrive by Promise — whatever has landed by submit time is sent.
  const ua = nav.userAgentData as UaData | undefined;
  stash(() =>
    ua?.getHighEntropyValues?.([
      'architecture',
      'bitness',
      'model',
      'platformVersion',
      'wow64',
      'formFactors',
    ]).then((values) => {
      state.highEntropy = values;
      return {};
    }),
  );
  stash(() =>
    (nav.getBattery as (() => Promise<{ level?: number; charging?: boolean }>) | undefined)
      ?.call(navigator)
      .then((battery) => ({
        battery_level: typeof battery.level === 'number' ? Math.round(battery.level * 100) : undefined,
        battery_charging: battery.charging,
      })),
  );
  stash(() =>
    navigator.mediaDevices?.enumerateDevices?.().then((devices) => {
      const count = (kind: string): number => devices.filter((d) => d.kind === kind).length;
      return {
        audio_inputs: count('audioinput'),
        audio_outputs: count('audiooutput'),
        video_inputs: count('videoinput'),
      };
    }),
  );
  stash(() =>
    navigator.storage?.estimate?.().then((estimate) => ({
      storage_quota_mb: Math.round((estimate.quota ?? 0) / 1048576),
      storage_usage_mb: Math.round((estimate.usage ?? 0) / 1048576),
    })),
  );
  for (const name of ['geolocation', 'notifications', 'camera', 'microphone']) {
    stash(() =>
      navigator.permissions
        ?.query?.({ name: name as PermissionName })
        .then((status) => ({ [`permission_${name}`]: status.state })),
    );
  }

  // Web vitals from buffered observers — entries recorded before this ran count.
  const watch = (options: PerformanceObserverInit, handle: (entries: PerformanceEntry[]) => void) => {
    try {
      new PerformanceObserver((entries) => handle(entries.getEntries())).observe(options);
    } catch {
      /* entry type unsupported = no key */
    }
  };
  watch({ type: 'largest-contentful-paint', buffered: true }, (entries) => {
    const last = entries[entries.length - 1];
    if (last) state.vitals.lcp = last.startTime;
  });
  watch({ type: 'layout-shift', buffered: true }, (entries) => {
    for (const entry of entries as (PerformanceEntry & { value?: number; hadRecentInput?: boolean })[]) {
      if (!entry.hadRecentInput) state.vitals.cls += entry.value ?? 0;
    }
  });
  watch({ type: 'event', buffered: true, durationThreshold: 16 } as PerformanceObserverInit, (entries) => {
    for (const entry of entries as (PerformanceEntry & { interactionId?: number })[]) {
      if (entry.interactionId) state.vitals.inp = Math.max(state.vitals.inp ?? 0, entry.duration);
    }
  });
  watch({ type: 'paint', buffered: true }, (entries) => {
    for (const entry of entries) {
      if (entry.name === 'first-contentful-paint') state.vitals.fcp = entry.startTime;
    }
  });

  // ── RUM: how hard the main thread was blocked (the camera steps run ML here) ─
  watch({ type: 'longtask', buffered: true }, (entries) => {
    const rum = state.rum;
    for (const entry of entries) {
      rum.longTasks += 1;
      rum.longTaskMs += entry.duration;
      rum.longTaskMaxMs = Math.max(rum.longTaskMaxMs, entry.duration);
      rum.blockingMs += Math.max(0, entry.duration - 50);
    }
  });
  watch({ type: 'long-animation-frame', buffered: true } as PerformanceObserverInit, (entries) => {
    const rum = state.rum;
    for (const entry of entries as (PerformanceEntry & { blockingDuration?: number })[]) {
      rum.loafs += 1;
      rum.loafBlockingMs += entry.blockingDuration ?? 0;
      rum.loafMaxMs = Math.max(rum.loafMaxMs, entry.duration);
    }
  });
  watch({ type: 'resource', buffered: true }, (entries) => {
    for (const entry of entries) noteResource(entry as PerformanceResourceTiming);
  });
  // Capture phase: a failed <img>/<script> fires `error` on the element and does
  // not bubble, so only a capturing listener on window sees it.
  window.addEventListener(
    'error',
    // `Event`, not the `ErrorEvent` the DOM typings promise: a resource failure
    // arrives here as a plain Event whose target is the failed element.
    (event: Event) => {
      if (event instanceof ErrorEvent) {
        state.rum.jsErrors += 1;
        const where = event.filename ? ` @ ${event.filename}:${event.lineno}:${event.colno}` : '';
        noteError(event.error instanceof Error ? event.error.name : 'Error', `${event.message}${where}`);
      } else {
        state.rum.resourceErrors += 1;
        const target = event.target as { src?: unknown; href?: unknown; tagName?: unknown } | null;
        const source = typeof target?.src === 'string' ? target.src : typeof target?.href === 'string' ? target.href : '';
        const tag = typeof target?.tagName === 'string' ? target.tagName.toLowerCase() : '?';
        noteError(`ResourceError<${tag}>`, source);
      }
    },
    true,
  );
  window.addEventListener('unhandledrejection', (event) => {
    state.rum.rejections += 1;
    const reason: unknown = event.reason;
    noteError(
      'UnhandledRejection',
      reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason),
    );
  });
}

// ------------------------------------------------------------- the snapshot

const pace = (p: Pace) => ({
  keystroke_count: p.keystrokes,
  input_count: p.inputs,
  paste_count: p.pastes,
  correction_count: p.corrections,
  focus_change_count: p.focusChanges,
  tap_count: p.taps,
  pointer_move_count: p.pointerMoves,
  scroll_max_percent: p.scrollMaxPercent,
  resize_count: p.resizes,
  hidden_count: p.hiddenCount,
  hidden_ms: p.hiddenMs,
  blurred_ms: p.blurredMs,
  idle_ms: p.idleMs,
  idle_max_ms: p.idleMaxMs,
});

/**
 * Everything observable right now, for the act named `eventType`. By default the
 * snapshot belongs to an ACT: it bumps the act sequence and resets the per-step
 * counters, so the next act's `form` section covers only its own step. A RUM
 * beacon passes `{ act: false }` — it reports the step in progress and must not
 * steal its pace from the submit that may still follow.
 */
export function collectTelemetry(eventType: string, options: { act?: boolean } = {}): Telemetry {
  const act = options.act ?? true;
  const now = Date.now();
  const nav = navigator as unknown as Record<string, unknown>;
  const ua = nav.userAgentData as UaData | undefined;
  const he = state.highEntropy;
  const net = nav.connection as
    | { type?: string; effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean }
    | undefined;
  const memory = (performance as unknown as {
    memory?: { jsHeapSizeLimit?: number; totalJSHeapSize?: number; usedJSHeapSize?: number };
  }).memory;
  const mb = (bytes: number | undefined): number | undefined =>
    typeof bytes === 'number' && bytes > 0 ? Math.round(bytes / 1048576) : undefined;
  const on = (query: string): boolean => window.matchMedia?.(query).matches ?? false;
  if (act) state.sequence += 1;

  // Close any open hidden/blurred interval so this step is credited with it.
  if (state.hiddenSince !== null) {
    bump('hiddenMs', now - state.hiddenSince);
    state.hiddenSince = now;
  }
  if (state.blurredSince !== null) {
    bump('blurredMs', now - state.blurredSince);
    state.blurredSince = now;
  }

  const out: Telemetry = {
    schema: TELEMETRY_SCHEMA,
    event: section(() => ({
      type: text(eventType),
      occurred_at: iso(now),
      sdk: 'verify',
      sequence: state.sequence,
    })),
    identity: section(() => ({
      device_id: state.device.id,
      device_first_seen_at: iso(state.device.firstSeenAt),
      device_visits: state.device.visits,
      page_started_at: iso(state.pageStartedAt),
    })),
    // ── what the machine is — RAM, CPU, GPU, battery, sensors ────────────────
    device: section(() => ({
      platform: text(ua?.platform) ?? text(navigator.platform),
      platform_version: text(he.platformVersion),
      mobile_hint: ua?.mobile,
      model: text(he.model),
      architecture: text(he.architecture),
      bitness: text(he.bitness),
      form_factors: list(he.formFactors),
      wow64: typeof he.wow64 === 'boolean' ? he.wow64 : undefined,
      brands: text((ua?.brands ?? []).slice(0, 6).map((b) => `${b.brand} ${b.version}`).join(', ')),
      // RAM in GB as the browser declares it: Chromium only, rounded to a power
      // of two and capped (a coarse bucket, not the real size). The old spec text
      // capped it at 8; Chromium 154 reported 16 in the 2026-10-08 UI e2e.
      memory_gb_approx: typeof nav.deviceMemory === 'number' ? nav.deviceMemory : undefined,
      // The JS heap this tab may use and is using — Chromium only.
      js_heap_limit_mb: mb(memory?.jsHeapSizeLimit),
      js_heap_total_mb: mb(memory?.totalJSHeapSize),
      js_heap_used_mb: mb(memory?.usedJSHeapSize),
      logical_processors: navigator.hardwareConcurrency,
      max_touch_points: navigator.maxTouchPoints,
      cookies_enabled: navigator.cookieEnabled,
      online_hint: navigator.onLine,
      automation_hint: navigator.webdriver,
      vendor: text(navigator.vendor),
      pdf_viewer: typeof nav.pdfViewerEnabled === 'boolean' ? nav.pdfViewerEnabled : undefined,
      // a count only — plugin NAMES are a fingerprint, a count is a property
      plugins_count: navigator.plugins?.length,
      touch_events: 'ontouchstart' in window,
      api_bluetooth: 'bluetooth' in navigator,
      api_usb: 'usb' in navigator,
      api_serial: 'serial' in navigator,
      api_hid: 'hid' in navigator,
      api_xr: 'xr' in navigator,
      api_service_worker: 'serviceWorker' in navigator,
      api_web_share: 'share' in navigator,
      api_credentials: 'credentials' in navigator,
      ...state.async,
    })),
    // The camera the liveness step opened (`noteCamera`); absent before then.
    camera: section(() => ({ ...(state.camera ?? {}) })),
    // How Cloudflare saw this browser's connection; absent until (unless) it answered.
    cloudflare_trace: section(() => ({ ...(state.trace ?? {}) })),
    // The GPU the browser names through WEBGL_debug_renderer_info. Read, never
    // rendered-and-hashed; the context is released immediately.
    gpu: section(() => {
      const gl = document.createElement('canvas').getContext('webgl');
      if (!gl) return {};
      const info = gl.getExtension('WEBGL_debug_renderer_info');
      // Probe WebGL2 support on a second canvas (one canvas holds one context
      // type) and release BOTH: the camera steps need the GPU budget.
      const gl2 = document.createElement('canvas').getContext('webgl2');
      const found = {
        vendor: info ? text(gl.getParameter(info.UNMASKED_VENDOR_WEBGL)) : undefined,
        renderer: info ? text(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : undefined,
        webgl2: Boolean(gl2),
      };
      gl.getExtension('WEBGL_lose_context')?.loseContext();
      gl2?.getExtension('WEBGL_lose_context')?.loseContext();
      return found;
    }),
    // ── the screen, window, language, timezone and the applicant's settings ──
    display: section(() => {
      const orientation = (screen as unknown as { orientation?: { type?: string; angle?: number } })
        .orientation;
      const visual = window.visualViewport;
      const intl = Intl.DateTimeFormat().resolvedOptions() as unknown as Record<string, unknown>;
      const year = new Date().getFullYear();
      return {
        screen_width_px: screen.width,
        screen_height_px: screen.height,
        available_width_px: screen.availWidth,
        available_height_px: screen.availHeight,
        viewport_width_px: window.innerWidth,
        viewport_height_px: window.innerHeight,
        visual_viewport_scale: visual?.scale,
        device_pixel_ratio: window.devicePixelRatio,
        color_depth: screen.colorDepth,
        orientation: text(orientation?.type),
        orientation_angle: orientation?.angle,
        window_width_px: window.outerWidth,
        window_height_px: window.outerHeight,
        screen_extended: (screen as unknown as { isExtended?: boolean }).isExtended,
        language: text(navigator.language),
        languages: list(navigator.languages),
        timezone: text(intl.timeZone),
        utc_offset_minutes: -new Date().getTimezoneOffset(),
        utc_offset_january_minutes: -new Date(year, 0, 1).getTimezoneOffset(),
        utc_offset_july_minutes: -new Date(year, 6, 1).getTimezoneOffset(),
        calendar: text(intl.calendar),
        numbering_system: text(intl.numberingSystem),
        color_scheme: on('(prefers-color-scheme: dark)') ? 'dark' : 'light',
        reduced_motion: on('(prefers-reduced-motion: reduce)'),
        contrast_more: on('(prefers-contrast: more)'),
        forced_colors: on('(forced-colors: active)'),
        pointer: on('(pointer: fine)') ? 'fine' : on('(pointer: coarse)') ? 'coarse' : 'none',
        hover_capable: on('(hover: hover)'),
        standalone: on('(display-mode: standalone)'),
        in_iframe: window.self !== window.top,
        visibility_state: text(document.visibilityState),
        has_focus: document.hasFocus(),
      };
    }),
    connection: section(() => ({
      type: text(net?.type),
      effective_type: text(net?.effectiveType),
      downlink_mbps: net?.downlink,
      rtt_ms: net?.rtt,
      save_data: net?.saveData,
    })),
    // ── where the page is and how the applicant got here (token never kept) ──
    page: section(() => {
      let referrerHost: string | undefined;
      let referrerPath: string | undefined;
      try {
        const from = new URL(document.referrer);
        referrerHost = from.hostname;
        referrerPath = templatePath(from.pathname).slice(0, 200);
      } catch {
        /* no referrer */
      }
      const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      return {
        hostname: text(location.hostname),
        path: text(templatePath(location.pathname)),
        referrer_host: referrerHost,
        referrer_path: referrerPath,
        navigation_type: text(entry?.type),
        document_language: text(document.documentElement.lang),
        history_length: history.length,
      };
    }),
    acquisition: (() => {
      const kept = allowlistedQuery(location.search);
      return kept ?? {};
    })(),
    privacy: section(() => ({
      gpc: nav.globalPrivacyControl === true,
      do_not_track: nav.doNotTrack === '1' || nav.doNotTrack === 'yes',
    })),
    // ── how this step was filled in, and the whole session so far ────────────
    form: section(() => ({
      step_started_at: iso(state.stepStartedAt),
      elapsed_ms: now - state.stepStartedAt,
      ...pace(state.step),
    })),
    session: section(() => ({
      elapsed_ms: now - state.pageStartedAt,
      acts: state.sequence,
      ...pace(state.session),
    })),
    performance: section(() => {
      const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (!entry) return {};
      const ms = (value: number): number | undefined =>
        value > 0 ? Math.round(value * 100) / 100 : undefined;
      return {
        dns_ms: ms(entry.domainLookupEnd - entry.domainLookupStart),
        connect_ms: ms(entry.connectEnd - entry.connectStart),
        tls_ms: entry.secureConnectionStart > 0 ? ms(entry.connectEnd - entry.secureConnectionStart) : undefined,
        ttfb_ms: ms(entry.responseStart - entry.startTime),
        dom_interactive_ms: ms(entry.domInteractive - entry.startTime),
        dom_complete_ms: ms(entry.domComplete - entry.startTime),
        load_event_end_ms: ms(entry.loadEventEnd - entry.startTime),
        transfer_size_bytes: entry.transferSize,
        next_hop_protocol: text(entry.nextHopProtocol),
      };
    }),
    web_vitals: section(() => {
      const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      return {
        lcp_ms: state.vitals.lcp,
        inp_ms: state.vitals.inp,
        cls: Math.round(state.vitals.cls * 10000) / 10000,
        fcp_ms: state.vitals.fcp,
        ttfb_ms: entry && entry.responseStart > 0 ? Math.round(entry.responseStart) : undefined,
      };
    }),
    // ── RUM, cumulative since the page loaded ─────────────────────────────────
    rum: section(() => {
      const rum = state.rum;
      return {
        long_task_count: rum.longTasks,
        long_task_total_ms: Math.round(rum.longTaskMs),
        long_task_max_ms: Math.round(rum.longTaskMaxMs),
        total_blocking_time_ms: Math.round(rum.blockingMs),
        long_animation_frame_count: rum.loafs,
        long_animation_frame_blocking_ms: Math.round(rum.loafBlockingMs),
        long_animation_frame_max_ms: Math.round(rum.loafMaxMs),
        js_error_count: rum.jsErrors,
        unhandled_rejection_count: rum.rejections,
        resource_error_count: rum.resourceErrors,
        errors: [...rum.errors],
        resource_count: rum.resources,
        resource_transfer_bytes: rum.resourceBytes,
        resource_by_type: { ...rum.resourceByType },
        slowest_resource: text(rum.slowestResource),
        slowest_resource_ms: rum.slowestResourceMs || undefined,
      };
    }),
    // Latency per `/api` route this page called: count, mean, max, failures.
    api: (() => {
      const api: Section = {};
      for (const [route, t] of state.rum.api) {
        api[route] = {
          count: t.count,
          avg_ms: Math.round(t.total_ms / t.count),
          max_ms: Math.round(t.max_ms),
          failures: t.failures,
          ...(t.last_status > 0 ? { last_status: t.last_status } : {}),
        };
      }
      return api;
    })(),
  };

  if (act) {
    state.step = freshPace();
    state.stepStartedAt = now;
  }
  return out;
}

/** At most this many beacons per page load — leaving and returning to the tab
 *  repeatedly is ordinary, an unbounded stream of rows is not. */
const MAX_BEACONS = 5;
/** `visibilitychange → hidden` is usually followed by `pagehide` when the tab
 *  closes; one beacon covers both. */
const BEACON_GAP_MS = 2000;

export type BeaconReason = 'hidden' | 'pagehide';

/**
 * Send a RUM snapshot whenever the applicant leaves the page — switches app,
 * locks the phone, closes the tab — through `send` (the network half lives in
 * ./client). Returns the disarm function, for a React effect cleanup.
 */
export function armExitBeacon(send: (reason: BeaconReason, telemetry: Telemetry) => void): () => void {
  let sent = 0;
  let lastAt = -Infinity;
  const fire = (reason: BeaconReason): void => {
    const now = Date.now();
    if (sent >= MAX_BEACONS || now - lastAt < BEACON_GAP_MS) return;
    sent += 1;
    lastAt = now;
    try {
      send(reason, collectTelemetry(reason === 'hidden' ? 'PAGE_HIDDEN' : 'PAGE_EXIT', { act: false }));
    } catch {
      /* instrumentation never throws into the page */
    }
  };
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') fire('hidden');
  };
  const onPageHide = (): void => fire('pagehide');
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
  };
}

/** Test seam: zero the counters and re-read the stored device record, as a new
 *  page load would. Listeners stay attached (priming twice would double-count),
 *  so a test primes once and resets between cases. */
export function resetTelemetryForTest(): void {
  state.device = {};
  loadDevice(Date.now());
  state.sequence = 0;
  state.pageStartedAt = Date.now();
  state.stepStartedAt = Date.now();
  state.lastActivityAt = Date.now();
  state.session = freshPace();
  state.step = freshPace();
  state.async = {};
  state.highEntropy = {};
  state.vitals = { cls: 0 };
  state.rum = freshRum();
  state.hiddenSince = null;
  state.blurredSince = null;
  state.camera = null;
  state.trace = null;
}
